import { Box, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { PropertyTypesAPI } from '../../fetch';
import { useToastContext } from '../../hooks';
import { IPropertyType, PropertyTypeImplementation } from '../../models';

export const PropertyTypes: FC = () => {
  const { showToast } = useToastContext();

  const [propertyTypes, setPropertyTypes] = useState<IPropertyType[]>([]);
  const [isLoadingPropertyTypes, setIsLoadingPropertyTypes] = useState(false);
  const [isSavingPropertyTypes, setIsSavingPropertyTypes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeletingPropertyType, setIsDeletingPropertyType] = useState<false | IPropertyType>(
    false
  );

  const fetchPropertyTypes = useCallback(async () => {
    setIsLoadingPropertyTypes(true);
    try {
      setPropertyTypes(await PropertyTypesAPI.getAll());
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch property types.');
    } finally {
      setIsLoadingPropertyTypes(false);
    }
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchPropertyTypes();
  }, [fetchPropertyTypes]);

  const removeRow = (row: IPropertyType): void => {
    setPropertyTypes((state: IPropertyType[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };
  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Property Type',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IPropertyType; index: number } };
        }) => {
          const [propertyTypeName, setPropertyTypeName] = useState(original.name ?? '');
          return (
            <Box>
              <TextField
                fullWidth
                autoFocus
                inputProps={{
                  id: `${'Property-Type-Name'}-${index}`,
                }}
                placeholder={'Property Type Name'}
                value={propertyTypeName}
                onChange={event => {
                  setPropertyTypeName(event.target.value);
                }}
                onBlur={() => {
                  // Write changes
                  setPropertyTypes((state: IPropertyType[]) => {
                    if (state[index].name !== propertyTypeName) {
                      setHasChanges(true);
                    }

                    state[index].name = propertyTypeName;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'buildoutPropertyTypeId',
        Header: 'Buildout Property Type',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IPropertyType; index: number } };
        }) => {
          const [buildoutPropertyTypeId, setBuildoutPropertyTypeId] = useState<null | number>(
            original.buildoutPropertyTypeId
          );

          return (
            <Box>
              <NumberFormat
                fullWidth
                isNumericString
                decimalScale={0}
                size="small"
                allowNegative={false}
                customInput={TextField}
                value={buildoutPropertyTypeId}
                onValueChange={values => {
                  const value = parseInt(values.value, 10);
                  if (Number.isNaN(value)) return;
                  setBuildoutPropertyTypeId(value);
                }}
                onBlur={() => {
                  // Write changes
                  setPropertyTypes((state: IPropertyType[]) => {
                    if (state[index].buildoutPropertyTypeId !== buildoutPropertyTypeId) {
                      setHasChanges(true);
                    }

                    state[index].buildoutPropertyTypeId = buildoutPropertyTypeId;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netSuiteProductTypeId',
        Header: 'NetSuite Product Type',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IPropertyType; index: number } };
        }) => {
          const [netSuiteProductTypeId, setNetSuiteProductTypeId] = useState<null | number>(
            original.netSuiteProductTypeId
          );

          return (
            <Box>
              <NumberFormat
                fullWidth
                isNumericString
                decimalScale={0}
                size="small"
                allowNegative={false}
                customInput={TextField}
                value={netSuiteProductTypeId}
                onValueChange={values => {
                  const value = parseInt(values.value, 10);
                  if (Number.isNaN(value)) return;
                  setNetSuiteProductTypeId(value);
                }}
                onBlur={() => {
                  setPropertyTypes((state: IPropertyType[]) => {
                    if (state[index].netSuiteProductTypeId !== netSuiteProductTypeId) {
                      setHasChanges(true);
                    }
                    state[index].netSuiteProductTypeId = netSuiteProductTypeId;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
    ];
  }, []);

  return (
    <Page title="Property Types">
      <AdminTable
        data={propertyTypes}
        columns={columns}
        isLoading={isLoadingPropertyTypes}
        hasChanges={hasChanges}
        isSaving={isSavingPropertyTypes}
        onAdd={() => {
          // Add new empty row
          setPropertyTypes(state => state.concat(PropertyTypeImplementation.getDefault()));
          setHasChanges(true);
        }}
        onSave={async function upsertPropertyTypes() {
          setIsSavingPropertyTypes(true);
          try {
            const updatedList: IPropertyType[] = await PropertyTypesAPI.upsert(propertyTypes);
            setPropertyTypes(updatedList);
            setHasChanges(false);
            fetchPropertyTypes();
            showToast('success', 'Successfully updated property types.');
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update property types.';
            showToast('error', message);
          } finally {
            setIsSavingPropertyTypes(false);
          }
        }}
        onDelete={(original, _index) => {
          if (original.propertyTypeId === PropertyTypeImplementation.getDefault().propertyTypeId) {
            showToast('info', 'Removed unsaved property type');
            removeRow(original);
          } else {
            setIsDeletingPropertyType(original);
          }
        }}
      />
      {/** ConfirmationDialogue */}
      {!!isDeletingPropertyType && (
        <ConfirmationDialogue
          open
          id="delete-property-type"
          title={`Delete Property Type ${isDeletingPropertyType.name}`}
          onClose={() => setIsDeletingPropertyType(false)}
          onConfirm={async function deletePropertyType() {
            // Check if modal is open and has proper data
            if (!isDeletingPropertyType) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingPropertyType.propertyTypeId ===
              PropertyTypeImplementation.getDefault().propertyTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingPropertyType);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await PropertyTypesAPI.delete(isDeletingPropertyType.propertyTypeId);
                // Update state after only successful delete
                removeRow(isDeletingPropertyType);
                fetchPropertyTypes();
                showToast('success', 'Successfully deleted property type.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'property type');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingPropertyType(false);
          }}
        />
      )}
    </Page>
  );
};
function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
